/**
 * Formats duration in days to human-readable string
 * Uses fuzzy logic for common durations
 * @param days - Number of days
 * @returns Formatted string like "1 month", "3 months", "1 year"
 */
export function formatDuration(days: number): string {
  // Exact matches for common durations
  if (days === 7) return '1 week';
  if (days === 14) return '2 weeks';
  if (days === 30) return '1 month';
  if (days === 60) return '2 months';
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  if (days === 365) return '1 year';
  if (days === 730) return '2 years';

  // Fuzzy logic for approximate durations (±5 days tolerance)
  if (Math.abs(days - 30) <= 5) return '1 month';
  if (Math.abs(days - 90) <= 5) return '3 months';
  if (Math.abs(days - 180) <= 5) return '6 months';
  if (Math.abs(days - 365) <= 5) return '1 year';

  // Calculate approximate duration
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  } else if (days < 365) {
    const months = Math.round(days / 30);
    return months === 1 ? '1 month' : `${months} months`;
  } else {
    const years = Math.round(days / 365);
    return years === 1 ? '1 year' : `${years} years`;
  }
}

/**
 * Formats a subscription interval for display
 * @param interval - The interval type ('month', 'year', 'one_time', null, etc.)
 * @param intervalCount - The number of intervals (e.g., 3 for "3 months")
 * @param durationDays - Duration in days for one-time purchases
 * @returns Formatted string like "/month", "/year", "/3 months", or "(1 month access)"
 */
export function formatInterval(
  interval: string | null,
  intervalCount: number | null,
  durationDays: number | null = null
): string {
  // Handle one-time payments (interval is null or 'one_time')
  if (!interval || interval === 'one_time') {
    if (durationDays) {
      return `(${formatDuration(durationDays)} access)`;
    }
    return '(one-time)';
  }

  // Handle subscriptions
  if (intervalCount && intervalCount > 1) {
    return `/${intervalCount} ${interval}s`;
  }
  return `/${interval}`;
}

/**
 * Formats currency amount using Intl.NumberFormat with locale support
 * @param amount - The numeric amount
 * @param currency - ISO currency code (USD, SEK, EUR, GBP, etc.)
 * @param locale - Optional locale for formatting (defaults to 'sv-SE' for Swedish format)
 * @param options - Optional Intl.NumberFormat options
 * @returns Formatted currency string like "10 kr" (Swedish) or "$10.00" (US)
 */
export function formatCurrency(
  amount: number,
  currency: string,
  locale: string = 'sv-SE',
  options?: Intl.NumberFormatOptions
): string {
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  return new Intl.NumberFormat(locale, { ...defaultOptions, ...options }).format(amount);
}

/**
 * Format date string to localized short date
 * @param dateStr - ISO date string or undefined
 * @returns Formatted date or 'Never' if undefined
 */
export function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return 'Never';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid';
  }
}

/**
 * Format a source string to a human-readable display name
 * @param source - Source identifier (e.g., 'stripe', 'entitlement', 'local')
 * @returns Formatted source name (e.g., 'Stripe', 'Entitlement Token', 'Local Storage')
 */
export function formatSource(source: string | null | undefined): string {
  if (!source) return 'Unknown';

  const sourceMap: Record<string, string> = {
    stripe: 'Stripe',
    entitlement: 'Entitlement Token',
    local: 'Local Storage',
    cloud: 'Cloud',
  };

  return sourceMap[source.toLowerCase()] || source;
}

/**
 * Calculate days remaining until a future date
 * @param expiryDate - Date string (ISO format) or Date object
 * @returns Number of days remaining, or null if invalid/past date
 */
export function calculateDaysRemaining(expiryDate: string | Date | null | undefined): number | null {
  if (!expiryDate) return null;

  try {
    const expiry = typeof expiryDate === 'string' ? new Date(expiryDate) : expiryDate;

    if (isNaN(expiry.getTime())) {
      return null;
    }

    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : null;
  } catch {
    return null;
  }
}

/**
 * Format date string to localized date and time
 * @param dateStr - ISO date string or undefined
 * @returns Formatted date/time or 'Unknown' if undefined
 */
export function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return 'Unknown';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid';
  }
}
